import { setupModuleFederation } from "./moduleFederationSetup";

const getLocaleDataPluralRules = (lang: string) => {
	switch (lang) {
		case "bg-BG":
			return import("@formatjs/intl-pluralrules/locale-data/bg.js");
		case "cs-CZ":
			return import("@formatjs/intl-pluralrules/locale-data/cs.js");
		case "de-DE":
			return import("@formatjs/intl-pluralrules/locale-data/de.js");
		case "es-ES":
			return import("@formatjs/intl-pluralrules/locale-data/es.js");
		case "fr-FR":
			return import("@formatjs/intl-pluralrules/locale-data/fr.js");
		case "hr-HR":
			return import("@formatjs/intl-pluralrules/locale-data/hr.js");
		case "hu-HU":
			return import("@formatjs/intl-pluralrules/locale-data/hu.js");
		case "it-IT":
			return import("@formatjs/intl-pluralrules/locale-data/it.js");
		case "nl-NL":
			return import("@formatjs/intl-pluralrules/locale-data/nl.js");
		case "pl-PL":
			return import("@formatjs/intl-pluralrules/locale-data/pl.js");
		case "pt-PT":
			return import("@formatjs/intl-pluralrules/locale-data/pt.js");
		case "ro-MD":
		case "ro-RO":
			return import("@formatjs/intl-pluralrules/locale-data/ro.js");
		case "ru-KZ":
		case "ru-RU":
			return import("@formatjs/intl-pluralrules/locale-data/ru.js");
		case "sk-SK":
			return import("@formatjs/intl-pluralrules/locale-data/sk.js");
		case "sr-RS":
			return import("@formatjs/intl-pluralrules/locale-data/sr.js");
		case "tr-TR":
			return import("@formatjs/intl-pluralrules/locale-data/tr.js");
		case "uk-UA":
			return import("@formatjs/intl-pluralrules/locale-data/uk.js");
		case "en-PK":
		default:
			return import("@formatjs/intl-pluralrules/locale-data/en.js");
	}
};

const getLocaleDataRelativeTimeFormat = (lang: string) => {
	switch (lang) {
		case "bg-BG":
			return import("@formatjs/intl-relativetimeformat/locale-data/bg.js");
		case "cs-CZ":
			return import("@formatjs/intl-relativetimeformat/locale-data/cs.js");
		case "de-DE":
			return import("@formatjs/intl-relativetimeformat/locale-data/de.js");
		case "es-ES":
			return import("@formatjs/intl-relativetimeformat/locale-data/es.js");
		case "fr-FR":
			return import("@formatjs/intl-relativetimeformat/locale-data/fr.js");
		case "hr-HR":
			return import("@formatjs/intl-relativetimeformat/locale-data/hr.js");
		case "hu-HU":
			return import("@formatjs/intl-relativetimeformat/locale-data/hu.js");
		case "it-IT":
			return import("@formatjs/intl-relativetimeformat/locale-data/it.js");
		case "nl-NL":
			return import("@formatjs/intl-relativetimeformat/locale-data/nl.js");
		case "pl-PL":
			return import("@formatjs/intl-relativetimeformat/locale-data/pl.js");
		case "pt-PT":
			return import("@formatjs/intl-relativetimeformat/locale-data/pt.js");
		case "ro-MD":
		case "ro-RO":
			return import("@formatjs/intl-relativetimeformat/locale-data/ro.js");
		case "ru-KZ":
		case "ru-RU":
			return import("@formatjs/intl-relativetimeformat/locale-data/ru.js");
		case "sk-SK":
			return import("@formatjs/intl-relativetimeformat/locale-data/sk.js");
		case "sr-RS":
			return import("@formatjs/intl-relativetimeformat/locale-data/sr.js");
		case "tr-TR":
			return import("@formatjs/intl-relativetimeformat/locale-data/tr.js");
		case "uk-UA":
			return import("@formatjs/intl-relativetimeformat/locale-data/uk.js");
		case "en-PK":
		default:
			return import("@formatjs/intl-relativetimeformat/locale-data/en.js");
	}
};

const shouldPolyfillPluralRules = (locale: string | undefined) =>
	!!locale &&
	(!("PluralRules" in Intl) ||
		new Intl.PluralRules("en", { minimumFractionDigits: 2 }).select(1) === "one" ||
		Intl.PluralRules.supportedLocalesOf([locale]).length !== 1);

const hasResolvedOptionsNumberingSystem = (locale: string) => {
	try {
		return "numberingSystem" in new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).resolvedOptions();
	} catch {
		return false;
	}
};

const shouldPolyfillRelativeTimeFormat = (locale: string | undefined) =>
	!!locale &&
	(!("RelativeTimeFormat" in Intl) ||
		Intl.RelativeTimeFormat.supportedLocalesOf([locale]).length !== 1 ||
		!hasResolvedOptionsNumberingSystem(locale));

const shouldPolyfillConstructedStyleSheets = () => {
	return !("replace" in (window.CSSStyleSheet?.prototype ?? {})) || !("adoptedStyleSheets" in Document.prototype);
};

const polyfills = [import("./core-js")];

if (!window.fetch) {
	polyfills.push(import("whatwg-fetch"));
}

if (shouldPolyfillPluralRules(window.lang)) {
	polyfills.push(import("@formatjs/intl-pluralrules/polyfill.js"));
	polyfills.push(getLocaleDataPluralRules(window.lang));
}

if (shouldPolyfillRelativeTimeFormat(window.lang)) {
	polyfills.push(import("@formatjs/intl-relativetimeformat/polyfill.js"));
	polyfills.push(getLocaleDataRelativeTimeFormat(window.lang));
}

if (shouldPolyfillConstructedStyleSheets()) {
	polyfills.push(import("construct-style-sheets-polyfill"));
}

Promise.all([...polyfills, setupModuleFederation()]).then(() => import("./main"));
